import { useAtomValue } from 'jotai';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { useEffect, useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Clock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { socialAgentService } from '@/services/social-agent.service';
import {
	DEFAULT_REPLY_DELAY_MINUTES,
	MAX_REPLY_DELAY_MINUTES,
	REPLY_DELAY_PRESETS,
	clampReplyDelay,
	formatReplyDelay,
	hasReplyDelay,
	replyDelayFromFields,
	replyDelayProblem,
	splitReplyDelay
} from '../constants';
import type { ReplyDelay } from '../types';
import { DIALOG_SHELL, DIALOG_TITLE, FIELD, FIELD_HINT, FIELD_LABEL, PILL_BUTTON } from '../ui-chrome';

/**
 * Delay Reply — Figma 3512:4160, with the fixed list replaced by a duration picker.
 *
 * An account-level setting, not a per-rule field: one delay covers every auto-reply. It applies to
 * replies only, which the modal says itself — a hide or a delete held for ten minutes has let the
 * thing it was meant to remove sit in public for ten minutes.
 *
 * The chips are shortcuts into the same two fields rather than a second control, so there is one
 * value on screen at a time and a saved delay with no chip still shows exactly.
 */

interface DelayReplyDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Current setting, or null when replies go out immediately. */
	replyDelay: ReplyDelay | null;
	onSaved: (replyDelay: ReplyDelay | null) => void;
}

interface DelayFields {
	hoursText: string;
	minutesText: string;
}

const HOUR_IN_MINUTES = 60;

const fieldsFor = (delay: ReplyDelay): DelayFields => {
	const { hours, minutes } = splitReplyDelay(delay);

	return { hoursText: String(hours), minutesText: String(minutes).padStart(2, '0') };
};

/** A stored 0 or nothing stored both open switched off, showing the default ready to switch on. */
const seedFields = (delay: ReplyDelay | null): DelayFields =>
	fieldsFor(hasReplyDelay(delay) ? delay : DEFAULT_REPLY_DELAY_MINUTES);

interface DelayPresetsProps {
	selected: ReplyDelay | null;
	disabled: boolean;
	onPick: (delay: ReplyDelay) => void;
}

const DelayPresets = ({ selected, disabled, onPick }: DelayPresetsProps) => (
	<div className='flex flex-wrap gap-1.5' role='group' aria-label='Quick pick'>
		{REPLY_DELAY_PRESETS.map((preset) => (
			<Button
				key={preset}
				type='button'
				variant={selected === preset ? 'default' : 'outline'}
				className={cn(PILL_BUTTON, 'px-3.5')}
				aria-pressed={selected === preset}
				disabled={disabled}
				onClick={() => onPick(preset)}
			>
				{formatReplyDelay(preset)}
			</Button>
		))}
	</div>
);

interface DurationFieldProps {
	/** "Hours" or "Minutes" — the field's accessible name and the stem of its steppers'. */
	unit: string;
	value: string;
	disabled: boolean;
	onChange: (value: string) => void;
	onStep: (direction: 1 | -1) => void;
}

const STEPPER = 'h-7 w-[72px] rounded-lg border-gray-500 p-0 text-gray-800';

const DurationField = ({ unit, value, disabled, onChange, onStep }: DurationFieldProps) => (
	<div className='flex flex-col items-center gap-1.5'>
		<Button
			type='button'
			variant='outline'
			className={STEPPER}
			aria-label={`Increase ${unit.toLowerCase()}`}
			disabled={disabled}
			onClick={() => onStep(1)}
		>
			<ChevronUp className='size-4' />
		</Button>
		<Input
			inputMode='numeric'
			aria-label={unit}
			value={value}
			disabled={disabled}
			onChange={(event) => onChange(event.target.value)}
			className={cn(FIELD, 'h-12 w-[72px] px-0 text-center text-xl md:text-xl font-medium tabular-nums')}
		/>
		<Button
			type='button'
			variant='outline'
			className={STEPPER}
			aria-label={`Decrease ${unit.toLowerCase()}`}
			disabled={disabled}
			onClick={() => onStep(-1)}
		>
			<ChevronDown className='size-4' />
		</Button>
		<span className={FIELD_HINT}>{unit.toLowerCase()}</span>
	</div>
);

const summaryFor = (enabled: boolean, delay: ReplyDelay | null): string => {
	if (!enabled) return 'Replies are sent as soon as the agent decides.';
	if (delay === null) return 'Set a delay to see when replies will be sent.';

	return `Each reply is sent ${formatReplyDelay(delay)} after the comment is posted.`;
};

export const DelayReplyDialog = ({ open, onOpenChange, replyDelay, onSaved }: DelayReplyDialogProps) => {
	const [enabled, setEnabled] = useState(() => hasReplyDelay(replyDelay));
	const [fields, setFields] = useState(() => seedFields(replyDelay));
	const [saving, setSaving] = useState(false);

	// Re-seed when the dialog opens: a Cancel that left a half-made choice behind would show it
	// again next time as though it had been saved.
	useEffect(() => {
		if (!open) return;
		setEnabled(hasReplyDelay(replyDelay));
		setFields(seedFields(replyDelay));
	}, [open, replyDelay]);

	const selectedBusinessUnitId = useAtomValue(selectedBusinessUnitIdAtom);
	const buScope = useMemo(
		() => (selectedBusinessUnitId ? { bu_id: selectedBusinessUnitId } : {}),
		[selectedBusinessUnitId]
	);

	const problem = enabled ? replyDelayProblem(fields.hoursText, fields.minutesText) : null;
	const delay = enabled ? replyDelayFromFields(fields.hoursText, fields.minutesText) : null;

	// Steppers work from what the fields spell; unreadable fields step from zero rather than not at all.
	const stepBy = (minutes: number) => {
		const current = replyDelayFromFields(fields.hoursText, fields.minutesText) ?? 0;
		setFields(fieldsFor(clampReplyDelay(current + minutes)));
	};

	const save = async () => {
		if (saving || problem) return;
		setSaving(true);

		// Saving while a brand is selected writes THAT brand's delay; a brand that was inheriting
		// the account's stops inheriting from here on.
		const saved = await socialAgentService.setReplyDelay(delay, buScope).catch((err: unknown) => {
			console.error('[DelayReplyDialog.save] setReplyDelay failed', { replyDelay: delay, err });
			toast.error(getApiErrorMessage(err, 'Could not save the reply delay'));
			return null;
		});

		setSaving(false);
		if (!saved) return;

		onSaved(saved.replyDelay);
		toast.success(saved.replyDelay ? 'Reply delay saved' : 'Replies will send immediately');
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{/* Same shell as the rule modal — one design-system dialog, one set of numbers:
			    rounded-[20px], a header divided by a rule and no fill, a 16px-inset body, and a
			    12px footer. Figma 4247:79282. */}
			<DialogContent showCloseButton={false} className={cn('sm:max-w-[600px]', DIALOG_SHELL)}>
				<DialogHeader className='flex-row items-center justify-between gap-3 border-b border-border px-3 py-2'>
					<div className='flex flex-col gap-1'>
						<DialogTitle className={DIALOG_TITLE}>Delay Reply</DialogTitle>
						<DialogDescription className={FIELD_HINT}>
							This will only apply when sending a reply.
						</DialogDescription>
					</div>
					<Switch checked={enabled} onCheckedChange={setEnabled} aria-label='Delay replies' />
				</DialogHeader>

				<div className='flex flex-col gap-5 px-4 py-6'>
					<div className='flex flex-col gap-2'>
						<span className={FIELD_LABEL}>Quick pick</span>
						<DelayPresets selected={delay} disabled={!enabled} onPick={(preset) => setFields(fieldsFor(preset))} />
					</div>

					<div className='flex flex-col gap-2'>
						<span className={FIELD_LABEL}>Or set your own</span>
						<div className='flex items-center gap-3'>
							<DurationField
								unit='Hours'
								value={fields.hoursText}
								disabled={!enabled}
								onChange={(hoursText) => setFields((current) => ({ ...current, hoursText }))}
								onStep={(direction) => stepBy(direction * HOUR_IN_MINUTES)}
							/>
							<span className='pb-5 text-xl font-medium text-gray-800' aria-hidden='true'>
								:
							</span>
							<DurationField
								unit='Minutes'
								value={fields.minutesText}
								disabled={!enabled}
								onChange={(minutesText) => setFields((current) => ({ ...current, minutesText }))}
								onStep={(direction) => stepBy(direction)}
							/>
							<div className='ml-3 flex-1 self-center rounded-[12px] bg-gray-100 px-3.5 py-3'>
								<p className={cn(FIELD_HINT, 'flex items-center gap-1')}>
									<Clock className='size-3.5' aria-hidden='true' /> What happens
								</p>
								<p className='mt-1 text-[13px] leading-5 text-ink'>{summaryFor(enabled, delay)}</p>
							</div>
						</div>
						{/* Always rendered so the dialog does not change height as a problem comes and goes. */}
						<p role='alert' className='min-h-4 text-[11px] leading-4 text-destructive'>
							{problem}
						</p>
					</div>
				</div>

				<div className='flex items-center justify-between gap-1.5 p-3'>
					<span className={FIELD_HINT}>
						From 1 min to {formatReplyDelay(MAX_REPLY_DELAY_MINUTES)}
					</span>
					<div className='flex gap-1.5'>
						<Button variant='outline' className={PILL_BUTTON} onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button className={PILL_BUTTON} onClick={save} disabled={saving}>
							{saving && <Loader2 className='size-4 animate-spin' />} Save
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
